import { injector } from '../../ng-injector'

export const CSRF_ERROR = 'Invalid CSRF token'
export interface CookieUtil {
  get(string): string
}

export type FetchFn = (
  input: RequestInfo,
  init?: RequestInit
) => Promise<Response>

export interface FetchOptions {
  method: string
  body?: any
  headers?: HeadersInit
  formData?: FormData
}

export type FetchDecorator = (
  url: string,
  options: FetchOptions
) => Promise<Response>

export interface Location {
  hash: string
  replace: (url: string) => void
}

export interface LocalStorage {
  setItem(key: string, value: string): void
}

export function hashStartsWithValidOrgUUID(hash: string): boolean {
  return /#\/[\w\d]{8}-[\w\d]{4}-[\w\d]{4}-[\w\d]{4}-[\w\d]{12}\//.test(hash)
}

const signinPageBaseHash = '#/pages/signin'

export function isSigninRoute(hash: string) {
  return hash.startsWith(signinPageBaseHash)
}

const buildHeaders = ({
  cookies,
  formData,
  headers
}: {
  cookies: CookieUtil
  formData: FormData | undefined
  headers: HeadersInit | undefined
}) => {
  const sharedHeaders = { 'X-XSRF-TOKEN': cookies.get('XSRF-TOKEN') }
  const finalHeaders = formData
    ? { ...sharedHeaders }
    : {
        ...sharedHeaders,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...headers
      }
  return new Headers(finalHeaders)
}

const setBody = ({
  init,
  formData,
  body
}: {
  init: RequestInit
  formData: FormData | undefined
  body: any
}) => {
  if (formData) {
    init.body = formData
  } else if (body) {
    init.body = JSON.stringify(body)
  }
}

const handleUnauthenticatedResponse = ({
  location,
  localStorage,
  res
}: {
  location: Location
  localStorage: LocalStorage | null
  res: Response
}) => {
  if (localStorage && !isSigninRoute(location.hash)) {
    localStorage.setItem('previousUrl', location.hash.slice(1))
  }
  location.hash = `${signinPageBaseHash}?unauthenticated=true`
  return res
}

const isCustom401Case = ({
  res,
  location
}: {
  res: Response
  location: Location
}) => {
  return res.status === 401 && hashStartsWithValidOrgUUID(location.hash)
}

const handleForbiddenResponse = ({
  location,
  res
}: {
  location: Location
  res: Response
}) => {
  location.replace('#/pages/403')
  return res
}

const isCustom403Case = ({
  res,
  location
}: {
  res: Response
  location: Location
}) => {
  return res.status === 403 && !isSigninRoute(location.hash)
}

export const decorateFetch = (
  fetch: FetchFn,
  cookies: CookieUtil,
  location: Location | null,
  localStorage: LocalStorage | null
): FetchDecorator => {
  if (!location) {
    throw new Error('location is required for decorateFetch')
  }

  return async (url: string, { body, method, headers, formData }) => {
    const init: RequestInit = {
      method,
      credentials: 'include',
      headers: buildHeaders({ cookies, formData, headers })
    }

    setBody({ init, formData, body })

    const res = await fetch(url, init)

    if (res.ok) {
      return res
    }

    const customCaseProps = { res, location }

    if (isCustom401Case(customCaseProps)) {
      return handleUnauthenticatedResponse({ ...customCaseProps, localStorage })
    }

    const response = await res.json()
    if (isCustom403Case(customCaseProps)) {
      if (
        Array.isArray(response?.messages) &&
        response?.messages?.includes(CSRF_ERROR)
      ) {
        // if the CSRF failed, we need to trigger a new login
        injector.invoke([
          'logoutService',
          (logoutService) => logoutService.logoutWithoutRedirect()
        ])
        handleUnauthenticatedResponse({ localStorage, location, res })
      } else {
        handleForbiddenResponse({ location, res })
      }
    }

    throw new HTTPError(
      `http error: status=${res.status} url=${url}`,
      res.status,
      response
    )
  }
}

export interface FieldErrorItem {
  field: string
  message: string
}

export interface BaseApiResponse {
  success: boolean
  messages: string[]
}

export interface BaseApiFormResponse extends BaseApiResponse {
  errors?: FieldErrorItem[]
}

export class HTTPError extends Error {
  public status: number
  public data: BaseApiFormResponse

  constructor(message: string, status: number, data: BaseApiFormResponse) {
    super()
    this.message = message
    this.status = status
    this.data = data
  }
}
